import ModalProvider from "@repo/components/src/globalProviders/modal";
import { VStack } from "@repo/components/src/ui/vstack";
import {
  CampaignAnalyticFragmentFragment,
  CampaignMediaFragmentFragment,
} from "@repo/graphql/documents/campaign/fragment.generated";
import React, { useCallback, useMemo } from "react";
import Hero from "../shared/hero";
import Section from "../shared/section";
import { ReportCampaign } from "../shared/types";
import BrandImpactSection from "./brandImpactSection";
import CommentsSection from "./commentsSection";
import CreatorsTable from "./creatorsTable";
import DeliverableModal from "./deliverableModal";
import Deliverables, { DeliverableItem } from "./deliverables";
import Demographics from "./demographics";
import EcommerceImpactSection from "./ecommerceImpactSection";
import { isScreenshotReportedDeliverable } from "./helpers";
import KpiGrid from "./kpiGrid";
import ProgressStrip from "./progressStrip";
import ProjectionSection from "./projectionSection";

type Props = {
  campaign: ReportCampaign;
  medias: readonly CampaignMediaFragmentFragment[];
  report: CampaignAnalyticFragmentFragment;
  campaignId: string;
  heroActions?: React.ReactNode;
  brandId?: string;
};

const OrganicReport = React.memo(
  ({ campaign, medias, report, campaignId, heroActions, brandId }: Props) => {
    const deliverableItems = useMemo<DeliverableItem[]>(
      () =>
        report.workOrders.flatMap((workOrder) =>
          workOrder.details.map((detail) => ({
            id: detail.id,
            workOrder,
            detail,
          })),
        ),
      [report],
    );

    // The public share route renders OrganicReport without a brandId (see
    // app/share/report/[token]/index.tsx) — that's the same signal already
    // used below to gate BrandImpactSection/EcommerceImpactSection, reused
    // here to keep the embedded comments analyzer off an unauthenticated page.
    const openDeliverable = useCallback(
      (item: DeliverableItem) => {
        // A self-reported deliverable only shows the video and its metrics —
        // no comments or sentiment — so it doesn't need the full-width layout.
        const isSelfReported = isScreenshotReportedDeliverable(item.detail);
        ModalProvider.show({
          children: (
            <DeliverableModal
              item={item}
              disableComments={!brandId}
              brandId={brandId}
            />
          ),
          size: isSelfReported ? "lg" : "2xl",
          contentProps: { className: "rounded-2xl h-[90%]" },
        });
      },
      [brandId],
    );

    return (
      <VStack space="md">
        <Hero campaign={campaign} medias={medias} actions={heroActions} />

        <ProgressStrip report={report} />

        <Section
          title="Métricas principales"
          description="Resultado consolidado de las piezas publicadas durante la ventana de campaña."
        >
          <KpiGrid report={report} />
        </Section>

        <ProjectionSection report={report} />

        <Section
          title="Contenido publicado"
          description="Click sobre cualquier pieza para ver métricas detalladas."
        >
          <Deliverables
            items={deliverableItems}
            rightOfUseMonths={campaign.rightOfUseMonths}
            onPress={openDeliverable}
          />
        </Section>

        <Section
          title="Demografía alcanzada"
          description="Reach prorrateado por la audiencia de cada creador, según los datos demográficos disponibles en sus cuentas vinculadas."
        >
          <Demographics report={report} />
        </Section>

        <Section
          title="Comentarios y sentimiento"
          description="Análisis automático sobre los comentarios extraídos de las publicaciones. Sentimiento clasificado en positivo, neutro y negativo."
        >
          <CommentsSection report={report} campaignId={campaignId} />
        </Section>

        {brandId ? (
          <BrandImpactSection report={report} brandId={brandId} />
        ) : null}

        {brandId ? (
          <EcommerceImpactSection report={report} brandId={brandId} />
        ) : null}

        <Section
          title="Detalle de creadores"
          description="Desempeño individual con desglose por publicación. Expande cada fila para ver los posts de cada creador; marca o desmarca creadores para recalcular los totales."
        >
          <CreatorsTable
            workOrders={report.workOrders}
            campaignId={campaignId}
          />
        </Section>
      </VStack>
    );
  },
);

OrganicReport.displayName = "OrganicReport";

export default OrganicReport;
